import { allocateCommittee, type CommitteeGroup, type CommitteeResult } from '@val/engine'
import { getParty } from '@val/shared'
import { useMemo, useState } from 'react'
import {
  assignParty,
  type Committee,
  getCommittees,
  getGroups,
  getPresidiumSettings,
  groupIndexByParty,
  MAX_GROUPS,
  newCommitteeId,
  type PresidiumConvention,
  presidiumSplit,
  setCommittees,
  setGroups,
  setPresidiumSettings,
} from '../lib/committees.ts'
import { int } from '../lib/format.ts'
import './sim.css'

interface Props {
  /** Simulerad mandatfördelning i KF. */
  seats: Record<string, number>
  highlightParty?: string
}

const ORDER = (a: string, b: string) => getParty(a).order - getParty(b).order

function groupLabel(parties: readonly string[]): string {
  return [...parties]
    .sort(ORDER)
    .map((c) => getParty(c).shortName)
    .join(' + ')
}

/** Färg = största partiet i gruppen. */
function groupColor(parties: readonly string[], seats: Record<string, number>): string {
  const lead = [...parties].sort((a, b) => (seats[b] ?? 0) - (seats[a] ?? 0))[0]
  return lead ? getParty(lead).color : 'var(--text-dim)'
}

function toCommitteeGroups(groups: string[][]): CommitteeGroup[] {
  return groups.map((parties, i) => ({ id: `g${i}`, parties }))
}

/** "3" eller, när lotten avgör, "3–4". */
const range = (min: number, max: number) => (min === max ? `${min}` : `${min}–${max}`)

interface GroupTotal {
  id: string
  parties: string[]
  groupVotes: number
  ledamoter: number
  ledamoterMax: number
  ersattare: number
  ersattareMax: number
}

export function CommitteeSeats({ seats, highlightParty }: Props) {
  const [groups, setGroupsState] = useState<string[][]>(() => getGroups())
  const [committees, setCommitteesState] = useState<Committee[]>(() => getCommittees())
  const [presidium, setPresidiumState] = useState(() => getPresidiumSettings())

  const partiesWithSeats = useMemo(
    () =>
      Object.keys(seats)
        .filter((c) => (seats[c] ?? 0) > 0)
        .sort(ORDER),
    [seats],
  )

  const idxByParty = groupIndexByParty(groups)
  const groupCount = Math.min(MAX_GROUPS, groups.length + 1)

  const updateGroups = (next: string[][]) => {
    setGroupsState(next)
    setGroups(next)
  }
  const updateCommittees = (next: Committee[]) => {
    setCommitteesState(next)
    setCommittees(next)
  }
  const updatePresidium = (patch: Partial<typeof presidium>) => {
    const next = { ...presidium, ...patch }
    setPresidiumState(next)
    setPresidiumSettings(next)
  }

  const cg = useMemo(() => toCommitteeGroups(groups), [groups])

  /** Id på den (normaliserade) grupp som vårt parti tillhör. */
  const myGroupId = useMemo(() => {
    if (!highlightParty) return null
    const gi = groups.findIndex((g) => g.includes(highlightParty))
    return gi >= 0 ? `g${gi}` : `solo:${highlightParty}`
  }, [groups, highlightParty])

  // Summerar ledamöter och ersättare per grupp över alla nämnder nedan –
  // ledamöter och ersättare tillsätts som två skilda proportionella val, så
  // de räknas var för sig och summeras sist.
  const groupTotals = useMemo(() => {
    const byId = new Map<string, GroupTotal>()
    for (const c of committees) {
      const led = allocateCommittee(seats, cg, c.seats)
      const ers = allocateCommittee(seats, cg, c.substitutes)
      for (const g of led.groups) {
        const t = byId.get(g.id) ?? {
          id: g.id,
          parties: g.parties,
          groupVotes: g.groupVotes,
          ledamoter: 0,
          ledamoterMax: 0,
          ersattare: 0,
          ersattareMax: 0,
        }
        t.ledamoter += g.minSeats
        t.ledamoterMax += g.maxSeats
        byId.set(g.id, t)
      }
      for (const g of ers.groups) {
        const t = byId.get(g.id) ?? {
          id: g.id,
          parties: g.parties,
          groupVotes: g.groupVotes,
          ledamoter: 0,
          ledamoterMax: 0,
          ersattare: 0,
          ersattareMax: 0,
        }
        t.ersattare += g.minSeats
        t.ersattareMax += g.maxSeats
        byId.set(g.id, t)
      }
    }
    return [...byId.values()].sort((a, b) => b.groupVotes - a.groupVotes)
  }, [seats, cg, committees])

  const totals = useMemo(
    () => ({
      ledamoter: committees.reduce((a, c) => a + c.seats, 0),
      ersattare: committees.reduce((a, c) => a + c.substitutes, 0),
      presidium: committees.reduce((a, c) => a + c.presidium, 0),
    }),
    [committees],
  )

  // Styrande grupp måste finnas bland dagens grupper – annars en kvarglömd
  // gammal inställning efter att grupperna ändrats.
  const styrandeGroup = groupTotals.find((g) => g.id === presidium.styrandeGroupId) ?? null
  const styrandeLabel = styrandeGroup ? groupLabel(styrandeGroup.parties) : null

  const presidiumTotals = useMemo(() => {
    if (!styrandeLabel) return null
    let styrande = 0
    let opposition = 0
    for (const c of committees) {
      for (const p of presidiumSplit(c.presidium, presidium.convention)) {
        if (p.styrande) styrande++
        else opposition++
      }
    }
    return { styrande, opposition }
  }, [committees, presidium.convention, styrandeLabel])

  return (
    <div className="committee">
      <p className="dim" style={{ marginTop: 0 }}>
        Platser i en nämnd fördelas <strong>mellan de valtekniska samverkansgrupperna</strong> med
        proportionellt val – heltalsmetoden, delningstal 1, 2, 3 … Röstetalet för en grupp är summan
        av partiernas KF-mandat. Metoden gynnar stora grupper; en grupp utan samverkanspartner får
        ofta inga platser. Ledamöter och ersättare tillsätts som två skilda val. Hur en grupps
        platser sedan fördelas mellan partierna förhandlas inom gruppen och visas inte här.
      </p>

      {/* --- Samverkansgrupper --- */}
      <div className="card__title">Valteknisk samverkan</div>
      <div className="committee__groups">
        {partiesWithSeats.map((code) => {
          const meta = getParty(code)
          const cur = idxByParty[code] ?? -1
          return (
            <div
              key={code}
              className={`committee__grouprow${code === highlightParty ? ' is-hi' : ''}`}
            >
              <span className="pb-chip" style={{ background: meta.color, color: meta.onColor }}>
                {meta.shortName}
              </span>
              <span className="tnum dim committee__kf">{seats[code] ?? 0} mandat</span>
              <div className="committee__seg">
                <button
                  type="button"
                  className={`committee__segbtn${cur === -1 ? ' is-on' : ''}`}
                  onClick={() => updateGroups(assignParty(groups, code, -1))}
                >
                  Fristående
                </button>
                {Array.from({ length: groupCount }, (_, i) => i).map((gi) => (
                  <button
                    type="button"
                    key={`grupp-${gi}`}
                    className={`committee__segbtn${cur === gi ? ' is-on' : ''}`}
                    onClick={() => updateGroups(assignParty(groups, code, gi))}
                  >
                    Grupp {gi + 1}
                  </button>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      <div className="committee__summary">
        {groups.length === 0 && (
          <span className="dim">Inga grupper – alla partier står ensamma.</span>
        )}
        {groups.map((g, i) => (
          <span
            key={g.join('+')}
            className="committee__gtag"
            style={{ borderColor: groupColor(g, seats) }}
          >
            <strong>Grupp {i + 1}:</strong> {groupLabel(g)}{' '}
            <span className="tnum dim">{g.reduce((a, c) => a + (seats[c] ?? 0), 0)} mandat</span>
          </span>
        ))}
      </div>

      {/* --- Nämndlista --- */}
      <div className="card__title" style={{ marginTop: 18 }}>
        Nämnder
      </div>
      <div className="committee__scroll">
        <table className="committee__table">
          <thead>
            <tr>
              <th>Nämnd</th>
              <th className="tnum">Ledamöter</th>
              <th className="tnum">Ersättare</th>
              <th className="tnum">{myGroupId ? 'Din grupp' : 'Största grupp'}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {committees.map((c) => (
              <CommitteeRow
                key={c.id}
                committee={c}
                ledamotResult={allocateCommittee(seats, cg, c.seats)}
                ersattareResult={allocateCommittee(seats, cg, c.substitutes)}
                myGroupId={myGroupId}
                seats={seats}
                convention={presidium.convention}
                styrandeLabel={styrandeLabel}
                onChange={(patch) =>
                  updateCommittees(committees.map((x) => (x.id === c.id ? { ...x, ...patch } : x)))
                }
                onRemove={() => updateCommittees(committees.filter((x) => x.id !== c.id))}
              />
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="linkbtn"
        onClick={() =>
          updateCommittees([
            ...committees,
            {
              id: newCommitteeId(),
              name: 'Ny nämnd',
              seats: 9,
              substitutes: 9,
              presidium: 3,
              au: '',
            },
          ])
        }
      >
        + Lägg till nämnd
      </button>

      {/* --- Totalt per grupp --- */}
      <div className="card__title" style={{ marginTop: 18 }}>
        Totalt per grupp
      </div>
      <div className="committee__scroll">
        <table className="committee__table">
          <thead>
            <tr>
              <th>Grupp</th>
              <th className="tnum">Mandat</th>
              <th className="tnum">Ledamöter</th>
              <th className="tnum">Ersättare</th>
              <th className="tnum">Uppdrag totalt</th>
            </tr>
          </thead>
          <tbody>
            {groupTotals.map((g) => (
              <tr key={g.id} className={g.id === myGroupId ? 'is-hi' : ''}>
                <td>
                  <span
                    className="committee__gname"
                    style={{ borderColor: groupColor(g.parties, seats) }}
                  >
                    {groupLabel(g.parties)}
                  </span>
                </td>
                <td className="tnum dim">{g.groupVotes}</td>
                <td className={`tnum${g.ledamoterMax === 0 ? ' committee__zero' : ''}`}>
                  {range(g.ledamoter, g.ledamoterMax)}
                </td>
                <td className={`tnum${g.ersattareMax === 0 ? ' committee__zero' : ''}`}>
                  {range(g.ersattare, g.ersattareMax)}
                </td>
                <td className="tnum committee__hi">
                  {range(g.ledamoter + g.ersattare, g.ledamoterMax + g.ersattareMax)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="dim committee__presetnote">
        Summan av ledamöter och ersättare i nämnderna ovan: {int(totals.ledamoter)} ledamöter +{' '}
        {int(totals.ersattare)} ersättare = {int(totals.ledamoter + totals.ersattare)} uppdrag,
        varav {int(totals.presidium)} presidieposter (ordförande och vice ordförande).
      </p>
      {groupTotals.some(
        (g) => g.ledamoter !== g.ledamoterMax || g.ersattare !== g.ersattareMax,
      ) && (
        <p className="dim committee__presetnote">
          Ett spann (t.ex. 0–10) betyder att en eller flera platser avgörs genom{' '}
          <strong>lottning</strong> (exakt lika jämförelsetal). Lägsta talet gäller om gruppen
          förlorar alla lotter, högsta om den vinner alla – varje lott vinner en grupp på bekostnad
          av en annan.
        </p>
      )}

      {/* --- Presidium --- */}
      <div className="card__title" style={{ marginTop: 18 }}>
        Presidium
      </div>
      <p className="dim" style={{ marginTop: 0 }}>
        Presidieposterna är formellt majoritetsval, öppna för vem som helst – men i praktiken tar
        den styrande gruppen normalt 2 av 3 poster. Vilken vice-post oppositionen får har varierat i
        Ale: historiskt 1:e vice ordförande, enligt ett nytt (omtvistat) beslut i stället 2:e vice
        ordförande.
      </p>
      <div className="committee__presidiumctl">
        <label className="committee__metafield">
          Styrande grupp
          <select
            className="pill-select"
            value={presidium.styrandeGroupId ?? ''}
            onChange={(e) => updatePresidium({ styrandeGroupId: e.target.value || null })}
          >
            <option value="">Ingen vald</option>
            {groupTotals.map((g) => (
              <option key={g.id} value={g.id}>
                {groupLabel(g.parties)} ({g.groupVotes} mandat)
              </option>
            ))}
          </select>
        </label>
        <label className="committee__metafield">
          Konvention
          <select
            className="pill-select"
            value={presidium.convention}
            onChange={(e) => updatePresidium({ convention: e.target.value as PresidiumConvention })}
          >
            <option value="ny">Nytt beslut – 2:e vice till oppositionen</option>
            <option value="historisk">Hittills – 1:e vice till oppositionen</option>
            <option value="allt">Allt till styrande gruppen</option>
          </select>
        </label>
      </div>
      {presidiumTotals ? (
        <p className="dim committee__presetnote">
          Om <strong>{styrandeLabel}</strong> styr: {int(presidiumTotals.styrande)} presidieposter
          till dem, {int(presidiumTotals.opposition)} till oppositionen (av {int(totals.presidium)}{' '}
          totalt). Fördelningen mellan partierna inom varje grupp – och vem inom oppositionen som
          får sina poster – förhandlas separat.
        </p>
      ) : (
        <p className="dim committee__presetnote">Välj en styrande grupp för att se fördelningen.</p>
      )}
    </div>
  )
}

function CommitteeRow({
  committee,
  ledamotResult,
  ersattareResult,
  myGroupId,
  seats,
  convention,
  styrandeLabel,
  onChange,
  onRemove,
}: {
  committee: Committee
  ledamotResult: CommitteeResult
  ersattareResult: CommitteeResult
  myGroupId: string | null
  seats: Record<string, number>
  convention: PresidiumConvention
  styrandeLabel: string | null
  onChange: (patch: Partial<Committee>) => void
  onRemove: () => void
}) {
  const [open, setOpen] = useState(false)
  const mine = myGroupId ? ledamotResult.groups.find((g) => g.id === myGroupId) : null
  const majority = Math.floor(committee.seats / 2) + 1
  const hasTie = ledamotResult.hasTie || ersattareResult.hasTie
  const posts = presidiumSplit(committee.presidium, convention)

  return (
    <>
      <tr>
        <td>
          <button type="button" className="committee__expand" onClick={() => setOpen((o) => !o)}>
            {open ? '▾' : '▸'}
          </button>
          <input
            className="committee__nameinput"
            value={committee.name}
            onChange={(e) => onChange({ name: e.target.value })}
          />
          {hasTie && (
            <span
              className="tag"
              title="Sista platsen avgörs genom lottning – utfallet visas som spann"
            >
              lottning
            </span>
          )}
        </td>
        <td>
          <input
            className="committee__seatsinput tnum"
            type="number"
            min={1}
            max={101}
            value={committee.seats}
            onChange={(e) =>
              onChange({
                seats: Math.min(101, Math.max(1, Math.round(Number(e.target.value) || 1))),
              })
            }
          />
        </td>
        <td>
          <input
            className="committee__seatsinput tnum"
            type="number"
            min={0}
            max={101}
            value={committee.substitutes}
            onChange={(e) =>
              onChange({
                substitutes: Math.min(101, Math.max(0, Math.round(Number(e.target.value) || 0))),
              })
            }
          />
        </td>
        <td
          className={`tnum committee__hi${mine && mine.maxSeats === 0 ? ' committee__zero' : ''}`}
        >
          {mine ? range(mine.minSeats, mine.maxSeats) : (ledamotResult.groups[0]?.seats ?? '–')}
        </td>
        <td>
          <button type="button" className="linkbtn linkbtn--danger" onClick={onRemove}>
            Ta bort
          </button>
        </td>
      </tr>
      {open && (
        <tr className="committee__detailrow">
          <td colSpan={5}>
            <div className="committee__detail">
              {ledamotResult.groups.map((g) => {
                const ers = ersattareResult.groups.find((x) => x.id === g.id)
                return (
                  <div
                    key={g.id}
                    className={`committee__gcol${g.id === myGroupId ? ' is-hi' : ''}`}
                  >
                    <span
                      className="committee__gname"
                      style={{ borderColor: groupColor(g.parties, seats) }}
                    >
                      {groupLabel(g.parties)}
                    </span>
                    <div className="committee__gseats">
                      <strong className="tnum">{range(g.minSeats, g.maxSeats)}</strong>
                      <span className="dim tnum"> ledamot</span>
                    </div>
                    <div className="committee__gseats">
                      <strong className="tnum">
                        {range(ers?.minSeats ?? 0, ers?.maxSeats ?? 0)}
                      </strong>
                      <span className="dim tnum"> ersättare</span>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="committee__meta">
              <label className="committee__metafield">
                Presidieposter (ordf. + vice)
                <input
                  className="committee__seatsinput tnum"
                  type="number"
                  min={0}
                  max={9}
                  value={committee.presidium}
                  onChange={(e) =>
                    onChange({
                      presidium: Math.min(9, Math.max(0, Math.round(Number(e.target.value) || 0))),
                    })
                  }
                />
              </label>
              <label className="committee__metafield committee__metafield--grow">
                Utskott (t.ex. arbetsutskott)
                <input
                  className="committee__nameinput committee__auinput"
                  value={committee.au}
                  placeholder="Inget utskott"
                  onChange={(e) => onChange({ au: e.target.value })}
                />
              </label>
            </div>

            {posts.length > 0 && (
              <ul className="committee__presidiumlist">
                {posts.map((p) => (
                  <li key={p.role}>
                    <span>{p.role}</span>
                    <strong>
                      {styrandeLabel
                        ? p.styrande
                          ? styrandeLabel
                          : 'Oppositionen'
                        : p.styrande
                          ? 'Styrande (ej vald)'
                          : 'Opposition (ej vald)'}
                    </strong>
                  </li>
                ))}
              </ul>
            )}

            <p className="committee__detailsum dim">
              Majoritet bland ledamöterna: {majority} av {int(committee.seats)}. Presidieposterna
              väljs genom majoritetsval, inte proportionellt – fördelningen mellan partierna inom
              varje grupp förhandlas separat.
            </p>
          </td>
        </tr>
      )}
    </>
  )
}
